import { useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListAttorneysQuery } from '@/services/api/attorneysService';

import { AddAttorneyDialog } from './AddAttorneyDialog';

type Props = {
  firmId: string;
  value: string | null | undefined;
  onChange: (attorneyId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  labelId?: string;
};

export const AttorneySelect = ({
  firmId,
  value,
  onChange,
  placeholder = 'Select attorney',
  disabled,
  id,
  labelId,
}: Props) => {
  const {
    data: attorneys = [],
    isLoading,
    refetch,
  } = useListAttorneysQuery({ firm_id: firmId }, { skip: !firmId });

  const options = useMemo(
    () =>
      attorneys.map((a) => ({
        id: a.id,
        label:
          `${a.first_name} ${a.last_name}${a.email ? ` (${a.email})` : ''}`.trim(),
      })),
    [attorneys],
  );

  const handleChange = (val: string) => {
    if (val === '__none__') {
      onChange(null);
    } else {
      onChange(val);
    }
  };

  const handleCreated = async (createdId: string) => {
    await refetch();
    onChange(createdId);
  };

  return (
    <div className="space-y-1.5">
      <Select
        value={value ?? undefined}
        onValueChange={handleChange}
        disabled={disabled || isLoading}
      >
        <SelectTrigger
          id={id}
          className="w-full"
          aria-label={placeholder}
          aria-labelledby={labelId}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">None</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.id} value={opt.id}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <AddAttorneyDialog
        firmId={firmId}
        onCreated={handleCreated}
        triggerLabel="+ Add new attorney"
        disabled={!firmId}
      />
    </div>
  );
};
